import {
  AppsTwoTone,
  AutoAwesomeTwoTone,
  CancelTwoTone,
  CloseTwoTone,
  FlightTwoTone,
  PlaceTwoTone,
  SearchTwoTone,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Slide,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onlineTourPropertySearch } from '../api';
import accommodation from '../assets/icons/accommodation.svg';
import { useDebounce } from '../hooks/useDebounce';
import { IOnlineTourSearchPropertyResult } from '../utils/types';
import { TransitionProps } from '@mui/material/transitions';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function FromAutoCompleteDialogContainer(props: {
  value: IOnlineTourSearchPropertyResult | null;
  onChange: (val: IOnlineTourSearchPropertyResult) => void;
  variant?: 'standard' | 'outlined';
  open: boolean;
  onClose: () => void;
}) {
  const variant = props.variant || 'standard';
  const [loading, setLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const debouncedSearchInput = useDebounce(searchInput, 300);
  const [onlinePropertySearch, setOnlinePropertySearch] = useState<
    IOnlineTourSearchPropertyResult[]
  >([]);
  const [error, setError] = useState<string>();
  const { t } = useTranslation();
  const acRef = createRef<HTMLElement | null>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchPropertySearchData = async () => {
    setLoading(true);
    try {
      const res = await onlineTourPropertySearch(searchInput);
      const data = await res.json();
      if (res?.status === 200) {
        setOnlinePropertySearch(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // TODO
      setError(e.detail || t('loginPage.registerErrorMessage'));
      console.log(error); //TODO
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertySearchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  useEffect(() => {
    setTimeout(() => {
      if (props.open && acRef?.current) {
        const input = acRef?.current?.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    }, 300);
  }, [acRef, props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
    >
      <DialogTitle>
        {t('fromAutoCompleteContainer.title')}
        <IconButton
          onClick={() => props.onClose()}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseTwoTone />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ height: 540, position: 'relative', pt: 2 }}>
        <Box sx={{ position: 'absolute', top: 1, width: '100%', height: 5 }}>
          {loading && <LinearProgress />}
        </Box>
        <Autocomplete
          inputValue={searchInput}
          options={onlinePropertySearch}
          value={props.value}
          loading={loading}
          onChange={(e, val) => {
            if (val) {
              props.onChange(val);
              props.onClose();
            }
          }}
          onInputChange={(e, newInputValue) => {
            setSearchInput(newInputValue);
            if (newInputValue.length >= 2) {
              setOnlinePropertySearch([]);
            } else if (newInputValue.length == 0) {
              setOnlinePropertySearch([]);
            }
          }}
          ref={acRef}
          getOptionLabel={(option) => option.searchText}
          renderInput={(params) => (
            <TextField
              {...params}
              variant={variant}
              placeholder={t('searchTour.searchPlaceholder')}
              color="secondary"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchTwoTone />
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li style={{ minHeight: 44 }} {...props}>
              {option.type.toLowerCase().includes('category') ? (
                <AppsTwoTone sx={{ mr: 2 }} fontSize="small" />
              ) : option.type.toLowerCase().includes('tour_concept') ||
                option.type.toLowerCase().includes('tour_product_types') ? (
                <AutoAwesomeTwoTone sx={{ mr: 2 }} fontSize="small" />
              ) : option.type.toLowerCase().includes('departure_cities') ||
                option.type.toLowerCase().includes('acc_cities') ? (
                <PlaceTwoTone sx={{ mr: 2 }} fontSize="small" />
              ) : option.type.toLowerCase().includes('departure_airports') ? (
                <FlightTwoTone sx={{ mr: 2 }} fontSize="small" />
              ) : (
                <img src={accommodation} alt="" height="24px" width="24px" />
              )}
              {option.searchText}
            </li>
          )}
          fullWidth
          openOnFocus
        />
      </DialogContent>
    </Dialog>
  );
}
